import { COMMUNITY_ROUTES } from '~/constants/community';
import type { SignupFlowStep } from '~/interfaces/signup_flow';
import buildCommunityModalPath from '~/utils/buildCommunityModalPath';

// Rejects protocol-relative / scheme-confusion paths (`//evil`, `/\evil`) that browsers may normalize off-origin.
export const isSafePath = (path: string | null | undefined): path is string => {
  if (!path || !path.startsWith('/')) return false;
  if (path[1] === '/' || path[1] === '\\') return false;
  return true;
};

export const COMMUNITY_DESTINATION_PARAM = 'destination';

export const isCommunityPath = (path: string | null | undefined): path is string => {
  const cleaned = path?.replace(/[\t\n\r]/g, '');
  if (!isSafePath(cleaned)) return false;

  const base = 'https://community.invalid';
  try {
    const { origin, pathname } = new URL(cleaned, base);
    return origin === base && (pathname === COMMUNITY_ROUTES.root || pathname.startsWith(`${COMMUNITY_ROUTES.root}/`));
  } catch {
    return false;
  }
};

export const resolveCommunityDestination = (params: URLSearchParams): string | null => {
  const path = params.get(COMMUNITY_DESTINATION_PARAM) ?? params.get('redirect_to');
  return isCommunityPath(path) ? path : null;
};

export const buildLoginPath = (): string => {
  if (typeof window === 'undefined') return '/login';
  return `/login?postSlug=${encodeURIComponent(window.location.pathname)}`;
};

export const buildCommunityLoginPath = (url: URL): string => {
  const destination = url.searchParams.get('redirect_to') ?? url.pathname + url.search;
  return buildCommunityModalPath('community_login', isSafePath(destination) ? destination : COMMUNITY_ROUTES.root);
};

// Reads `?postSlug=` set by paywall sign-in links; strips `modal` + `postSlug` from preserved params.
export const buildPostAuthRedirectUrl = (
  currentStep?: SignupFlowStep,
  nextStep?: SignupFlowStep,
): string | undefined => {
  if (typeof document === 'undefined') return;

  if (currentStep && nextStep) {
    try {
      const current = new URL(currentStep.url);
      const modal = currentStep.modal_param || current.searchParams.get('modal');
      if (modal === 'login' || current.pathname === '/login') {
        const next = new URL(nextStep.url);
        if (nextStep.modal_param) next.searchParams.set('modal', nextStep.modal_param);
        return `${document.location.origin}${next.pathname}${next.search}`;
      }
    } catch {}
  }

  const currentSearchParams = new URLSearchParams(document.location.search);
  const postSlug = currentSearchParams.get('postSlug');

  currentSearchParams.delete('modal');
  currentSearchParams.delete('postSlug');

  const path = isSafePath(postSlug) ? postSlug : document.location.pathname;
  const stringifiedParams = currentSearchParams.toString();

  return stringifiedParams
    ? `${document.location.origin}${path}?${stringifiedParams}`
    : `${document.location.origin}${path}`;
};
